/**
 * The «Группы» client — contracts #26.8–#26.13 (ADDITIVE v1.9.5, T.Z50/T.Z51), talking
 * to the live `apps/api/src/curator/groups.service.ts`.
 *
 *   #26.8   GET    /api/curator/groups                                → `{data:{groups:[…]}}`
 *   #26.9   POST   /api/curator/groups                                → `{data:{group}}`
 *   #26.10  PATCH  /api/curator/groups/{groupId}                      → `{data:{group}}`
 *   #26.11  DELETE /api/curator/groups/{groupId}                      → 204
 *   #26.12  POST   /api/curator/groups/{groupId}/members              → `{data:{group}}`
 *   #26.13  DELETE /api/curator/groups/{groupId}/members/{childId}    → 204
 *
 * Every route sits behind the SAME cabinet guard as #26 — an id outside the session's
 * venues answers 403 `CURATOR_SCOPE_REQUIRED`, never an empty page. Reads AND writes
 * travel RAW (`unknown` out) and hand the FULL envelope to `curator-groups-model`,
 * where the unwrapping is pure logic the plain-Node `unit` project pins — T.Z61 found
 * the writes unwrapping twice (`requestEnvelope(['data'])` into a parser that reads
 * `{data:{group}}` again), which surfaced the api's own 201 as a client-side
 * «HTTP 500 (INTERNAL): Ответ сервера не содержит группы.»
 *
 * THE OWNER'S RULES on the wire (T.Z50): a group belongs to a venue; the 30-member cap
 * is HARD (`GROUP_FULL` + `error.meta.limit`); the педагог must be of the group's venue
 * (400). `groups-model` carries the RU copy for each refusal, so a full group says
 * «уберите кого-то» instead of showing a code.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

import { ApiError, apiRequest, buildQuery } from './client';
import {
  GROUP_ENVELOPE_MISSING_COPY,
  parseCuratorGroups,
  readGroupWriteEnvelope,
  withoutGroupMember,
  type CuratorGroup,
  type MemberRemoveInput,
} from './curator-groups-model';

export type { CuratorGroup, CuratorGroupMember } from './curator-groups-model';
export type { GroupCandidates, GroupCandidateSources, MemberRemoveInput } from './curator-groups-model';
export {
  candidateLabel,
  candidateMembers,
  canDeleteGroup,
  fullnessTone,
  groupCandidates,
  memberCountLabel,
  parseCuratorGroup,
  parseCuratorGroups,
  shortName,
  teacherFieldState,
  teacherLabel,
  withheldInGroupHint,
  withheldOnlineHint,
  withoutGroupMember,
} from './curator-groups-model';

export const CURATOR_GROUPS_PATH = '/curator/groups';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

function groupPath(groupId: number): string {
  return `${CURATOR_GROUPS_PATH}/${encodeURIComponent(groupId)}`;
}

function memberPath(groupId: number, applicationId: number): string {
  return `${groupPath(groupId)}/members/${encodeURIComponent(applicationId)}`;
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** #26.8 — the groups of the scope, or of one `?venueId=` of it (RAW out). */
export async function fetchCuratorGroups(venueId: number | undefined, signal?: AbortSignal): Promise<unknown> {
  return apiRequest<unknown>('GET', `${CURATOR_GROUPS_PATH}${buildQuery({ venueId })}`, { signal });
}

/** The unwrapping half of #26.8, in one place for the hook and the tests. */
export async function loadCuratorGroups(venueId: number | undefined, signal?: AbortSignal): Promise<CuratorGroup[]> {
  const body = await fetchCuratorGroups(venueId, signal);
  return parseCuratorGroups(body);
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/** Body of #26.9 — the venue decides where the group lands; the teacher is optional. */
export interface CuratorGroupCreateInput {
  venueId: number;
  name: string;
  teacherId?: number | null;
}

/** Body of #26.10 — only provided keys change; `teacherId: null` un-assigns. */
export interface CuratorGroupPatchInput {
  name?: string;
  teacherId?: number | null;
}

/**
 * The single-group write answer, RAW in — T.Z61. The body is the api's full
 * `{data:{group}}` envelope; the unwrap itself is {@link readGroupWriteEnvelope} in
 * `curator-groups-model`, where the unit project pins it against exactly what
 * `groups.routes.spec.ts` proves #26.9/#26.10/#26.12 answer. Feeding this a
 * pre-unwrapped envelope was the bug: the parser looked for `.data` twice and the SPA
 * answered its own «Ответ сервера не содержит группы.» over a 201.
 */
async function groupOfWrite(body: unknown, method: string, path: string): Promise<CuratorGroup> {
  const result = readGroupWriteEnvelope(body);
  if (!result.ok) throw new ApiError(500, method, path, 'INTERNAL', GROUP_ENVELOPE_MISSING_COPY);
  return result.group;
}

/** #26.9 — the created group. */
export async function createCuratorGroup(input: CuratorGroupCreateInput): Promise<CuratorGroup> {
  const body = await apiRequest<unknown>('POST', CURATOR_GROUPS_PATH, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(input),
  });
  return groupOfWrite(body, 'POST', CURATOR_GROUPS_PATH);
}

/** #26.10 — rename / re-педагог. */
export async function patchCuratorGroup(groupId: number, input: CuratorGroupPatchInput): Promise<CuratorGroup> {
  const path = groupPath(groupId);
  const body = await apiRequest<unknown>('PATCH', path, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(input),
  });
  return groupOfWrite(body, 'PATCH', path);
}

/** #26.11 — soft-delete; the api refuses a group that still holds members (409). */
export async function deleteCuratorGroup(groupId: number): Promise<void> {
  await apiRequest<void>('DELETE', groupPath(groupId), { method: 'DELETE' });
}

/** #26.12 — assign a child; the api refuses #31 (409 GROUP_FULL). */
export async function addCuratorGroupMember(groupId: number, applicationId: number): Promise<CuratorGroup> {
  const path = `${groupPath(groupId)}/members`;
  const body = await apiRequest<unknown>('POST', path, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ applicationId }),
  });
  return groupOfWrite(body, 'POST', path);
}

/** #26.13 — remove a child. */
export async function removeCuratorGroupMember(groupId: number, applicationId: number): Promise<void> {
  await apiRequest<void>('DELETE', memberPath(groupId, applicationId), { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

const GROUPS_ROOT = ['curator', 'groups'] as const;

/** #26.8 — one venue's groups, only while a venue is picked (the section is venue-scoped). */
export function useCuratorGroups(venueId: number | null) {
  return useQuery({
    queryKey: [...GROUPS_ROOT, venueId],
    queryFn: ({ signal }) => loadCuratorGroups(venueId === null ? undefined : venueId, signal),
    enabled: venueId !== null,
    refetchOnWindowFocus: false,
    staleTime: 15_000,
    retry: 0,
  });
}

/**
 * #26.8 — the WHOLE scope's groups (no `?venueId=` — the api's own default), ADDITIVE
 * (v1.9.6, T.Z62): the working table's «Группа» filter needs options on «Все места»
 * too, where the venue-scoped hook stays idle BY DESIGN (`enabled: venueId !== null`,
 * the group section's rule) — a separate key so the two reads never share a cache
 * entry, and a scope-wide read cannot leak into a venue-scoped list.
 */
export function useCuratorGroupsScope() {
  return useQuery({
    queryKey: [...GROUPS_ROOT, 'scope'] as const,
    queryFn: ({ signal }) => loadCuratorGroups(undefined, signal),
    refetchOnWindowFocus: false,
    staleTime: 15_000,
    retry: 0,
  });
}

function useInvalidateGroups(): () => void {
  const queryClient = useQueryClient();
  return () => {
    // A write moves a roster, a count AND — through the offline children's
    // `applicationId` candidates — the «Добавить» list, so the cabinet root re-reads.
    void queryClient.invalidateQueries({ queryKey: ['curator'] });
  };
}

export function useCuratorGroupCreate() {
  const invalidate = useInvalidateGroups();
  return useMutation({ mutationFn: createCuratorGroup, onSettled: invalidate });
}

export function useCuratorGroupPatch() {
  const invalidate = useInvalidateGroups();
  return useMutation({
    mutationFn: (input: { groupId: number; patch: CuratorGroupPatchInput }) =>
      patchCuratorGroup(input.groupId, input.patch),
    onSettled: invalidate,
  });
}

export function useCuratorGroupDelete() {
  const invalidate = useInvalidateGroups();
  return useMutation({ mutationFn: (groupId: number) => deleteCuratorGroup(groupId), onSettled: invalidate });
}

export function useCuratorGroupMemberAdd() {
  const invalidate = useInvalidateGroups();
  return useMutation({
    mutationFn: (input: { groupId: number; applicationId: number }) =>
      addCuratorGroupMember(input.groupId, input.applicationId),
    onSettled: invalidate,
  });
}

/**
 * T.Z84 — the OPTIMISTIC half of #26.13, as a function of the cache alone so the
 * plain-Node `unit` project can pin it against a real `QueryClient`: snapshot every
 * cached groups list, paint `{@link withoutGroupMember}` over each one, and hand back
 * the closure that puts the originals back.
 *
 * Every key under `['curator', 'groups']` is patched — the venue-scoped list the
 * group card reads AND the scope-wide one the ЦА table's «Группа» filter reads
 * (T.Z62) — because a member left one roster and both say so.
 */
export function patchMemberRemoved(queryClient: QueryClient, input: MemberRemoveInput): () => void {
  const snapshot = queryClient.getQueriesData<CuratorGroup[]>({ queryKey: [...GROUPS_ROOT] });
  for (const [key, groups] of snapshot) {
    if (groups === undefined) continue;
    queryClient.setQueryData(key, withoutGroupMember(groups, input));
  }
  return () => {
    for (const [key, groups] of snapshot) {
      if (groups === undefined) continue;
      queryClient.setQueryData(key, groups);
    }
  };
}

/**
 * #26.13 — T.Z84: the line leaves the table the moment «Убрать» is clicked, not after
 * the round trip. The order the react-query docs prescribe, and why each step is here:
 *
 *   * `onMutate` CANCELS the in-flight list reads first — a refetch landing mid-write
 *     would answer the pre-removal roster and paint the row straight back over the
 *     optimistic state — then snapshots and patches ({@link patchMemberRemoved}),
 *     returning the rollback as the mutation's context.
 *   * `onError` rolls the cache back to that snapshot. The SERVER'S copy of the
 *     refusal surfaces through the caller's own `onError` (the group card's error
 *     line, `curatorErrorCopy`) — react-query runs the hook-level callbacks first and
 *     the `mutate(...)`-level ones after, so the user reads why the row came back
 *     instead of watching it flicker.
 *   * `onSettled` still re-reads `['curator']`, success or failure: the optimistic
 *     state is a guess, the server's roster is the truth, and the candidates list and
 *     the scope-wide key ride the same invalidation they always did.
 */
export function useCuratorGroupMemberRemove() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateGroups();
  return useMutation({
    mutationFn: (input: MemberRemoveInput) => removeCuratorGroupMember(input.groupId, input.applicationId),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: [...GROUPS_ROOT] });
      return patchMemberRemoved(queryClient, input);
    },
    onError: (_cause, _input, rollback) => rollback?.(),
    onSettled: invalidate,
  });
}
